import {
  locateTrackPosition,
  sampleCenterlineAhead,
  type Track,
} from '../track/TrackLoader';

export interface AutopilotLineSample {
  arc: number;
  x: number;
  z: number;
  tx: number;
  tz: number;
  nx: number;
  nz: number;
  widthLeft: number;
  widthRight: number;
  signedCurvature: number;
  curvature: number;
  desiredLateralOffset: number;
  targetSpeed: number;
  lookahead: number;
}

export interface AutopilotLine {
  samples: AutopilotLineSample[];
  spacing: number;
  totalLength: number;
}

export interface AutopilotLineOptions {
  spacing?: number;
  lateralAcceleration?: number;
  maxSpeed?: number;
  minSpeed?: number;
  brakingAcceleration?: number;
  acceleration?: number;
  safetyMargin?: number;
  laneBias?: number;
}

const DEFAULT_SPACING = 3;
const DEFAULT_LATERAL_ACCELERATION = 125;
const DEFAULT_MIN_SPEED = 18;
const DEFAULT_MAX_SPEED = 46;
const DEFAULT_BRAKING_ACCELERATION = 42;
const DEFAULT_ACCELERATION = 30;
const CURVATURE_EPSILON = 1e-4;

const lineCache = new WeakMap<Track, AutopilotLine>();
const optionLineCache = new WeakMap<Track, Map<string, AutopilotLine>>();

export function getAutopilotLine(track: Track, options: AutopilotLineOptions = {}): AutopilotLine {
  if (Object.keys(options).length === 0) {
    const cached = lineCache.get(track);
    if (cached) return cached;
  } else {
    const key = cacheKey(options);
    let trackCache = optionLineCache.get(track);
    if (!trackCache) {
      trackCache = new Map();
      optionLineCache.set(track, trackCache);
    }
    const cached = trackCache.get(key);
    if (cached) return cached;
    const line = buildAutopilotLine(track, options);
    trackCache.set(key, line);
    return line;
  }
  const line = buildAutopilotLine(track, options);
  if (Object.keys(options).length === 0) lineCache.set(track, line);
  return line;
}

function cacheKey(options: AutopilotLineOptions): string {
  return [
    options.spacing,
    options.lateralAcceleration,
    options.maxSpeed,
    options.minSpeed,
    options.brakingAcceleration,
    options.acceleration,
    options.safetyMargin,
    options.laneBias,
  ].join(':');
}

export function buildAutopilotLine(track: Track, options: AutopilotLineOptions = {}): AutopilotLine {
  const spacing = options.spacing ?? DEFAULT_SPACING;
  const count = Math.max(16, Math.ceil(track.totalLength / spacing));
  const actualSpacing = track.totalLength / count;
  const maxSpeed = options.maxSpeed ?? DEFAULT_MAX_SPEED;
  const minSpeed = options.minSpeed ?? DEFAULT_MIN_SPEED;
  const lateralAcceleration = options.lateralAcceleration ?? DEFAULT_LATERAL_ACCELERATION;
  const safetyMargin = options.safetyMargin ?? 1.25;
  const laneBias = options.laneBias ?? 0;

  const samples: AutopilotLineSample[] = [];
  for (let i = 0; i < count; i++) {
    const arc = i * actualSpacing;
    const center = sampleCenterlineAhead(track, 0, arc);
    const pos = locateTrackPosition(track, center.x, center.z);
    const signedCurvature = signedCurvatureAt(track, arc, actualSpacing * 2);
    const curvature = Math.abs(signedCurvature);
    const usableHalfWidth = Math.max(1, Math.min(pos.widthLeft, pos.widthRight) - safetyMargin);
    const desiredLateralOffset = desiredLaneOffset(pos.widthLeft, pos.widthRight, signedCurvature, laneBias, safetyMargin);
    const curvatureSpeed = curvature > CURVATURE_EPSILON
      ? Math.sqrt(lateralAcceleration / curvature)
      : maxSpeed;
    const widthSpeed = Math.sqrt(Math.max(1, usableHalfWidth) / Math.max(0.001, curvature || CURVATURE_EPSILON)) * 0.9;
    const targetSpeed = clamp(Math.min(curvatureSpeed, widthSpeed, maxSpeed), minSpeed, maxSpeed);
    samples.push({
      arc,
      x: center.x,
      z: center.z,
      tx: center.tx,
      tz: center.tz,
      nx: pos.nx,
      nz: pos.nz,
      widthLeft: pos.widthLeft,
      widthRight: pos.widthRight,
      signedCurvature,
      curvature,
      desiredLateralOffset,
      targetSpeed,
      lookahead: lookaheadFor(curvature, targetSpeed),
    });
  }

  smoothSpeeds(samples);
  applySpeedEnvelope(samples, actualSpacing, options);
  for (const sample of samples) sample.lookahead = lookaheadFor(sample.curvature, sample.targetSpeed);
  return { samples, spacing: actualSpacing, totalLength: track.totalLength };
}

function desiredLaneOffset(
  widthLeft: number,
  widthRight: number,
  signedCurvature: number,
  laneBias: number,
  safetyMargin: number,
): number {
  if (laneBias <= 0) return 0;
  const magnitude = clamp((Math.abs(signedCurvature) - 0.004) / 0.035, 0, 1);
  if (magnitude <= 0) return 0;
  const side = Math.sign(signedCurvature);
  const maxOffset = side > 0
    ? Math.max(0, widthLeft - safetyMargin)
    : -Math.max(0, widthRight - safetyMargin);
  return maxOffset * laneBias * 0.45 * magnitude;
}

export function sampleAutopilotLine(line: AutopilotLine, arc: number): AutopilotLineSample {
  const total = Math.max(1e-6, line.totalLength);
  const wrapped = ((arc % total) + total) % total;
  const index = Math.floor(wrapped / line.spacing) % line.samples.length;
  return line.samples[index];
}

export function sampleAutopilotLineAhead(line: AutopilotLine, arc: number, distance: number): AutopilotLineSample {
  return sampleAutopilotLine(line, arc + distance);
}

function applySpeedEnvelope(
  samples: AutopilotLineSample[],
  spacing: number,
  options: AutopilotLineOptions,
): void {
  const brake = options.brakingAcceleration ?? DEFAULT_BRAKING_ACCELERATION;
  const accel = options.acceleration ?? DEFAULT_ACCELERATION;
  for (let pass = 0; pass < 4; pass++) {
    for (let i = samples.length - 1; i >= 0; i--) {
      const next = samples[(i + 1) % samples.length];
      samples[i].targetSpeed = Math.min(
        samples[i].targetSpeed,
        Math.sqrt(next.targetSpeed * next.targetSpeed + 2 * brake * spacing),
      );
    }
    for (let i = 0; i < samples.length; i++) {
      const prev = samples[(i - 1 + samples.length) % samples.length];
      samples[i].targetSpeed = Math.min(
        samples[i].targetSpeed,
        Math.sqrt(prev.targetSpeed * prev.targetSpeed + 2 * accel * spacing),
      );
    }
  }
}

function smoothSpeeds(samples: AutopilotLineSample[]): void {
  for (let pass = 0; pass < 2; pass++) {
    const next = samples.map((sample, i) => {
      const prev = samples[(i - 1 + samples.length) % samples.length];
      const after = samples[(i + 1) % samples.length];
      return sample.targetSpeed * 0.5 + (prev.targetSpeed + after.targetSpeed) * 0.25;
    });
    for (let i = 0; i < samples.length; i++) samples[i].targetSpeed = next[i];
  }
}

function signedCurvatureAt(track: Track, arc: number, span: number): number {
  const a = sampleCenterlineAhead(track, arc, -span);
  const b = sampleCenterlineAhead(track, arc, span);
  return tangentAngleDelta(a.tx, a.tz, b.tx, b.tz) / Math.max(1, span * 2);
}

function lookaheadFor(curvature: number, speed: number): number {
  const speedLookahead = 6 + speed * 0.28;
  const curveScale = 1 - clamp((curvature - 0.01) / 0.06, 0, 0.55);
  return clamp(speedLookahead * curveScale, 6, 22);
}

function tangentAngleDelta(ax: number, az: number, bx: number, bz: number): number {
  return Math.atan2(ax * bz - az * bx, ax * bx + az * bz);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
